// components/nosDepenses/modals/AutoValidationCaisseDialog.jsx
// ============================================================================
// AUTO-VALIDATION À LA CAISSE CENTRALE
// ============================================================================
// Nul ne valide une caisse qu'il tient ou a clôturée, ni une dotation qu'il a
// demandée ou qu'il reçoit. Seul un super validateur peut passer outre : le
// serveur répond alors `AUTO_VALIDATION_REQUISE`, et cette fenêtre recueille
// la justification et le code de double authentification. L'action est
// inscrite au journal des actions privilégiées, marquée « auto-validée », et
// la direction est prévenue (backend2/routes/caisse-centrale.routes.js).
//
// `onConfirmer({ auto_validation, justification, code_totp })` renvoie la
// réponse du serveur ; en cas de refus (code faux…), la fenêtre reste ouverte
// et affiche le message.
// ============================================================================

import React, { useState } from 'react';
import { Loader2, ShieldAlert } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../ui/dialog';

const ZONE_TEXTE = 'w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15';
const BOUTON_PIED = 'inline-flex h-9 min-w-[168px] items-center justify-center gap-2 rounded-md px-4 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50';
const BOUTON_SECONDAIRE = `${BOUTON_PIED} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`;
const BOUTON_PRINCIPAL = `${BOUTON_PIED} bg-[#246990] text-white hover:bg-[#1f5a7a]`;

const AutoValidationCaisseDialog = ({
  open,
  libelle,
  raison,
  longueurJustification = 30,
  onClose,
  onConfirmer
}) => {
  const [justification, setJustification] = useState('');
  const [code, setCode] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');

  const longueur = justification.trim().length;
  const pret = longueur >= longueurJustification && /^\d{6}$/.test(code);

  const confirmer = async () => {
    if (!pret || enCours) return;
    setEnCours(true);
    setErreur('');
    try {
      const reponse = await onConfirmer({
        auto_validation: true,
        justification: justification.trim(),
        code_totp: code
      });
      if (reponse && reponse.success === false) {
        setErreur(reponse.message || 'Validation impossible.');
        setCode('');
      }
    } catch (e) {
      setErreur(e.message || 'Validation impossible.');
      setCode('');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(ouvert) => { if (!ouvert && !enCours) onClose(); }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Valider vous-même</DialogTitle>
          <DialogDescription>{libelle}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold">{raison}</p>
              <p className="mt-1 text-amber-800">
                Comme super validateur, vous pouvez le faire vous-même. L'action sera inscrite au journal des
                actions privilégiées, marquée « auto-validée », et signalée à la direction.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-[1fr_200px]">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">Justification</label>
              <textarea
                rows={3}
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                className={ZONE_TEXTE}
                placeholder="Pourquoi personne d'autre ne peut-il le faire ?"
              />
              <p className={`mt-1 text-xs ${longueur >= longueurJustification ? 'text-gray-500' : 'text-amber-700'}`}>
                {longueur} / {longueurJustification} caractères minimum
              </p>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">Code de double authentification</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-center text-sm tracking-[0.3em] outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                placeholder="000000"
              />
            </div>
          </div>

          {erreur && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{erreur}</p>
          )}
        </div>

        <DialogFooter>
          <button type="button" onClick={onClose} disabled={enCours} className={BOUTON_SECONDAIRE}>
            Annuler
          </button>
          <button type="button" onClick={confirmer} disabled={!pret || enCours} className={BOUTON_PRINCIPAL}>
            {enCours && <Loader2 className="h-4 w-4 animate-spin" />}
            Valider et journaliser
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AutoValidationCaisseDialog;
